import { ErrorIcon } from '@icons';
import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { ErrorText, H2, Input, Label, SuccessText, Text } from '@ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { usePostApi } from './postApi.hook';
import { getApplicantEmail } from '../../../api/lendingAPI/CustomerStateAPIHelper';
import { GreenTickStatusSVG } from '../../../svgComponents/greenTickStatusSVG/index';
import { InformationSVG } from '../../../svgComponents/informationSVG/index';

const mapStateToProps = (state: RootState) => ({
    accountEmail: getApplicantEmail(state)
});

const AccountDetails = ({
    accountEmail
}: ReturnType<typeof mapStateToProps>) => {
    const intl = useIntl();
    const { status, hasError, isLoading, triggerFetch } = usePostApi({
        endpoint: '/api/lending/edge/password/reset/'
    });

    return (
        <div className="space-y-xl">
            <H2>
                <FormattedMessage
                    id="_.your_company.account_details.title"
                    defaultMessage="Your account info"
                />
            </H2>

            <div className="space-y-l rounded-m border border-secondary-90 bg-structure-100 p-l">
                <div className="flex flex-col items-start space-y-s">
                    <Label htmlFor="account-email">
                        <FormattedMessage
                            id="_.your_company.account_details.account_email"
                            defaultMessage="Account Email"
                        />
                    </Label>
                    {/* eslint-disable-next-line iwoca/input-a11y */}
                    <Input
                        className="w-full max-w-xl"
                        id="account-email"
                        name="account-email"
                        disabled
                        value={accountEmail}
                    />
                </div>

                <div className="space-y-s">
                    <Text>
                        <FormattedMessage
                            id="_.your_company.account_details.set_new_password"
                            defaultMessage="Send a link to reset your password"
                        />
                    </Text>
                    <Button
                        onClick={() =>
                            triggerFetch({ data: { email: accountEmail } })
                        }
                        loading={isLoading}
                    >
                        <FormattedMessage
                            id="_.your_company.account_details.reset_password_cta"
                            defaultMessage="Reset password"
                        />
                    </Button>

                    {!hasError && status === 204 ? (
                        <div className="flex items-center">
                            <GreenTickStatusSVG className="mr-xs shrink-0" />
                            <SuccessText>
                                <FormattedMessage
                                    id="_.your_company.account_details.reset_password_success"
                                    defaultMessage="Check your email inbox; we've sent a link"
                                />
                            </SuccessText>
                        </div>
                    ) : null}

                    {hasError && status !== 204 ? (
                        <div className="flex items-center">
                            <ErrorIcon className="mr-xs shrink-0" />
                            <ErrorText>
                                <FormattedMessage
                                    id="_.your_company.account_details.reset_password_failure"
                                    defaultMessage="Sorry, something's gone wrong. Try again later?"
                                />
                            </ErrorText>
                        </div>
                    ) : null}
                </div>

                <div className="flex items-center">
                    <InformationSVG className="mr-xs shrink-0" />
                    <Text
                        className="text-s font-med"
                        dangerouslySetInnerHTML={{
                            __html: intl.formatMessage({
                                id: '_.your_company.account_details.change_email_info',
                                defaultMessage:
                                    "Want to change the email address on your account?\nGive us a ring on '<a href=\"tel:020 3397 3375\">020 3397 3375</a>' and we'll sort it."
                            })
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(AccountDetails);
